<template>
  <PaginaAdministrar
    titulo="Catálogo de Fornecedores"
    subtitulo="Gestão de part numbers, equações de compra, lead times e MOQ por parceiro"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="novoItem"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="podeVerAcoes"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <h3 class="filtros-titulo">Filtrar por:</h3>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label for="filtro-busca" class="form-label">Busca Livre (Part Number, Produto, Parceiro)</label>
          <input type="text" id="filtro-busca" class="form-control" v-model="filtros.busca" placeholder="Digite para buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <CampoBusca id="filtroFornecedor" label="Fornecedor Específico" v-model="filtros.idFornecedor" :configuracao-busca="fornecedorSearch" @selecionado="(item) => descricaoFornecedorFiltro = item?.nomeFantasia || item?.razaoSocial" />
        </div>
        <div class="col-md-4">
          <CampoBusca id="filtroProduto" label="Produto Master" v-model="filtros.idProduto" :configuracao-busca="configBuscaProduto" @selecionado="(item) => descricaoProdutoFiltro = item?.nome" />
        </div>
        <div class="col-12 d-flex justify-content-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum vínculo de compra cadastrado."
      row-key="idProdutoFornecedor"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editar($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idProdutoFornecedor }}</span>
      </template>
      <template #produto="{ row }">
        <div class="fw-bold text-body-emphasis">{{ row.skuProduto }}</div>
        <div class="small text-body-secondary text-truncate" style="max-width: 250px;" :title="row.nomeProduto">{{ row.nomeProduto }}</div>
      </template>

      <template #fornecedor="{ row }">
        <div class="fw-medium text-body-emphasis text-truncate" style="max-width: 200px;" :title="row.nomeFornecedor">{{ row.nomeFornecedor }}</div>
        <div class="small text-body-secondary">{{ row.documentoFornecedor }}</div>
      </template>

      <template #equacaoCompra="{ row }">
        <span class="font-monospace small">
          <span class="text-primary">{{ row.qtdUnidadeFornecedor }}</span> [{{ getUnidadeTexto(row.lstUnidadeFornecedor) }}] =
          <span class="text-success">{{ row.qtdUnidadeBase }}</span> [{{ getUnidadeTexto(row.lstUnidadeMedidaBase) }}]
        </span>
      </template>

      <template #leadTime="{ row }">
        <span class="text-center">{{ row.tempoFornecimento ? row.tempoFornecimento + ' d' : '-' }}</span>
      </template>

      <template #moq="{ row }">
        <span v-if="row.quantidadeMinima" class="badge bg-secondary me-1">MOQ: {{ row.quantidadeMinima }}</span>
        <span v-if="row.lstMoedaPadrao" class="badge bg-success">{{ getMoedaTexto(row.lstMoedaPadrao) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1" v-if="podeVerAcoes">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" @click="editar(row)" title="Editar Relação">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ProdutoFornecedor"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{
      'lstUnidadeFornecedor': listas.unidades,
      'lstMoedaPadrao': listas.moedas
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { fornecedorSearch } from '@/search-definitions/fornecedorSearch'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])
const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()
const configBuscaProduto = computed(() => criarConfigBuscaProduto())

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({ busca: '', idFornecedor: '', idProduto: '' })
const descricaoFornecedorFiltro = ref('')
const descricaoProdutoFiltro = ref('')

const listas = reactive({ unidades: [], moedas: [] })

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const podeVerAcoes = computed(() => true)

const colunas = [
  { field: 'idProdutoFornecedor', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'produto', label: 'Produto Base', sortable: false, slotName: 'produto' },
  { field: 'fornecedor', label: 'Fornecedor', sortable: false, slotName: 'fornecedor' },
  { field: 'codigoNoFornecedor', label: 'Part Number', width: '15%', sortable: true, class: 'font-monospace small' },
  { field: 'equacao', label: 'Equação de Compra', sortable: false, slotName: 'equacaoCompra' },
  { field: 'tempoFornecimento', label: 'Lead Time', width: 100, align: 'center', sortable: true, slotName: 'leadTime' },
  { field: 'moq', label: 'MOQ / Moeda', width: 120, align: 'right', sortable: false, slotName: 'moq' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => { buscarDados(1) }

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else { lista.value = []; paginacao.value.TotalCount = 0 }
})

const carregarListas = async () => {
  try {
    const [resUnidades, resMoedas] = await Promise.all([
      api.get('/ListasSistema/lookup/UN_MED'),
      api.get('/ListasSistema/lookup/MOE')
    ])
    listas.unidades = resUnidades.data
    listas.moedas = resMoedas.data
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

const getUnidadeTexto = (id) => {
  if (!id) return '-'
  const obj = listas.unidades.find(u => u.idListaSistemaItem === id || u.idListaClienteItem === id)
  return obj ? obj.textoLista : id
}

const getMoedaTexto = (id) => {
  if (!id) return '-'
  const obj = listas.moedas.find(m => m.idListaSistemaItem === id || m.idListaClienteItem === id)
  return obj ? obj.textoLista : id
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.idFornecedor) params.append('idFornecedor', filtros.idFornecedor)
    if (filtros.idProduto) params.append('idProduto', filtros.idProduto)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/ProdutoFornecedores?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar:', error)
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.idFornecedor = ''; filtros.idProduto = ''; descricaoFornecedorFiltro.value = ''; descricaoProdutoFiltro.value = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const novoItem = () => { emit('adicionar-item') }
const editar = (item) => { emit('editar-item', item) }

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idProdutoFornecedor
  auditoriaSelecionada.nome = item.skuProduto + ' - ' + item.nomeFornecedor
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
